/*
 * Copyright (C) Motorola Solutions, INC.
 * All Rights Reserved.
 */

import { Injectable } from '@angular/core';
import { KeycloakService } from './keycloak-service';

/**
 * Used to provide user feedback on progress and status events during client initialization prior to the angular application bootstrap
 */
@Injectable({
    providedIn: 'root'
})
export class InitializationService {
    public static readonly INIT_ELEMENT_ID = 'initialization';
    public static readonly INIT_ERROR_ELEMENT_ID = 'initialization-error';
    public static readonly KEYCLOAK_ERROR_ELEMENT_ID = 'auth-error-wrapper';
    public static readonly INIT_STANDBY_ELEMENT_ID = 'initialization-standby-message';
    public static readonly INIT_STANDBY_ELEMENT_TEXT_ID =  'initialization-standby-message-text';
    public static readonly INIT_ERROR_LOGOUT_BUTTON_ID = '#recovery-signout-btn';
    private initializationBlock: HTMLElement;
    private initializationStandbyElement: HTMLElement;
    private initializationStandbyElementText: HTMLElement;
    private initializationErrorElement: HTMLElement;
    private logoutButton: HTMLButtonElement;

    private static readonly TEMPLATE =
    `<div id="initialization-standby-message">
          <div id="initialization-standby-message-text">Standby...</div>
          <img src="/assets/icons/spinner.svg" alt="loading"/>
      </div>
      <div id='initialization-error'></div>`;

    private static readonly KEYCLOAK_ERROR_TEMPLATE =
        `<div id="auth-error-wrapper">
              <div id="auth-error-inner-wrapper">
                  <svg width="128px" height="128px" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor" role="img" aria-label="error">
                      <path d="M3.60167 21.0002H20.3983C21.5538 21.0002 22.2754 19.7487 21.6965 18.7487L12.8654 3.49506C12.4803 2.82977 11.5197 2.82977 11.1346 3.49506L2.30353 18.7487C1.72458 19.7487 2.44617 21.0002 3.60167 21.0002ZM10.5 10.0002C10.5 9.44795 10.9477 9.00023 11.5 9.00023H12.5C13.0523 9.00023 13.5 9.44795 13.5 10.0002V15.5002C13.5 15.7764 13.2761 16.0002 13 16.0002H11C10.7239 16.0002 10.5 15.7764 10.5 15.5002V10.0002ZM10.5 18.0002C10.5 17.4479 10.9477 17.0002 11.5 17.0002H12.5C13.0523 17.0002 13.5 17.4479 13.5 18.0002C13.5 18.5525 13.0523 19.0002 12.5 19.0002H11.5C10.9477 19.0002 10.5 18.5525 10.5 18.0002Z"></path>
                  </svg>
                  <div id="auth-error-text">
                      <div id="auth-error-title">Sign in failed</div>
                      <p>The VESTA NXT sign in process has encountered a technical error. A recovery process has been initiated and may take a few moments to resolve the issue.</p>
                      <button id="recovery-signout-btn" class="msi-btn fail-recovery">Back to sign in</button>
                  </div>
              </div>
          </div>`;

    constructor(private keycloak: KeycloakService) {
        this.initializationBlock = document.getElementById(InitializationService.INIT_ELEMENT_ID);
        this.initializationBlock.classList.add('show');
        this.initializationBlock.innerHTML = InitializationService.TEMPLATE;
        this.initializationStandbyElement = document.getElementById(InitializationService.INIT_STANDBY_ELEMENT_ID);
        this.initializationStandbyElementText = document.getElementById(InitializationService.INIT_STANDBY_ELEMENT_TEXT_ID);
        this.initializationErrorElement = document.getElementById(InitializationService.INIT_ERROR_ELEMENT_ID);
    }

    public setInfo(text: string) {
        this.clearError();
        this.initializationStandbyElementText.textContent = text;
    }

    public setKeycloakError() {
        this.clearInfo();
        this.initializationErrorElement.classList.add('show');
        this.initializationErrorElement.innerHTML = InitializationService.KEYCLOAK_ERROR_TEMPLATE;
        if (this.keycloak.keycloak) {
            this.logoutButton = this.initializationErrorElement.querySelector(InitializationService.INIT_ERROR_LOGOUT_BUTTON_ID) as HTMLButtonElement;
            this.logoutButton.onclick = () => this.keycloak.keycloak?.logout();
            this.logoutButton.classList.add('show');
        }
    }

    public setError(err: string) {
        this.clearInfo();
        this.initializationErrorElement.textContent = err;
        this.initializationErrorElement.classList.add('show');
        if (this.keycloak.keycloak) {
            this.logoutButton.classList.add('show');
        }
    }

    public clearInfo() {
        this.initializationStandbyElement.classList.add('hide');
    }

    public clearError() {
        this.initializationErrorElement.classList.remove('show');
    }

    public clear() {
        this.initializationBlock.classList.add('hide');
    }
}
